import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PopupWrapper from '../../../../components/popup/PopupWrapper';
import { getButtonClass } from '../../../../components/popup/popupUtils';
import AutocompleteCombo from '../../../../components/widgets/AutocompleteCombo';
import Pagination from '../../../../components/widgets/Pagination';
import AuthorDisplay from '../../../../components/widgets/AuthorDisplay';
import Tabs from '../../../../components/widgets/Tabs';
import { useAuth } from '../../../../assets/contexts/AuthContext';
import { isGlowPlatformAdmin } from '../../../../assets/utils/comm/platformAdminUtils';
import { api } from '../../../../lib/api';
import { buildProjectDetailPath } from '../projects/comm/projectPortal';

/** 전문가/기관 선택 팝업용 기본 목록 (config.items 미제공 시 사용) */
const DEFAULT_EXPERT_ORG_ITEMS = [
  { id: 1, name: '한국AI연구소', type: '기업', field: 'AI/데이터', region: '수도권', email: 'contact@ai-research.kr', memberTypeLabel: '기업', skipUserFetch: true, authorCd: 'COMPANY' },
  { id: 2, name: '바이오헬스랩', type: '기업', field: '바이오/헬스', region: '충청권', email: 'info@biohealthlab.kr', memberTypeLabel: '기업', skipUserFetch: true, authorCd: 'COMPANY' },
  { id: 3, name: '김연구 박사', type: '개인', field: '소재/부품', region: '영남권', email: 'kim.research@example.com', memberTypeLabel: '개인', skipUserFetch: true, authorCd: 'INDIVIDUAL' },
];

/** 지원 제안 대상 API 페이지 크기 (서버 Pageable.size) */
const PROPOSAL_TARGETS_PAGE_SIZE = 5;

/** R&D 협력 제안 대상(/targets) — 레거시 Autocomplete용 전체 로드 시 서버 페이지 순회 병합 */
const RND_COLLAB_TARGETS_PAGE_SIZE = PROPOSAL_TARGETS_PAGE_SIZE;
const RND_COLLAB_TARGETS_MAX_PAGES = 500;
const TASK_LIST_PAGE_SIZE = 5;
const TASK_SOURCE_TECH_DEMAND = 'tech-demand';
const TASK_SOURCE_RESEARCH = 'research';
const TASK_SOURCE_COMMUNITY = 'community';

function listChunkFromCollaborationTargetsPage(res) {
  if (!res) return [];
  if (Array.isArray(res.list)) return res.list;
  if (Array.isArray(res.content)) return res.content;
  return [];
}

async function fetchAllRndCollaborationTargetsMerged(apiGet, baseParams) {
  const out = [];
  let pageNumber = 1;
  let totalPages = 1;
  while (pageNumber <= totalPages && pageNumber <= RND_COLLAB_TARGETS_MAX_PAGES) {
    const res = await apiGet({
      params: { ...baseParams, pageNumber, size: RND_COLLAB_TARGETS_PAGE_SIZE },
    });
    const chunk = listChunkFromCollaborationTargetsPage(res);
    out.push(...chunk);
    const tp = Number(res?.totalPages);
    if (Number.isFinite(tp) && tp >= 1) {
      totalPages = tp;
    } else {
      const te = Number(res?.totalElements);
      if (Number.isFinite(te) && RND_COLLAB_TARGETS_PAGE_SIZE > 0) {
        totalPages = Math.max(1, Math.ceil(te / RND_COLLAB_TARGETS_PAGE_SIZE));
      } else if (chunk.length < RND_COLLAB_TARGETS_PAGE_SIZE) {
        break;
      } else {
        totalPages = pageNumber + 1;
      }
    }
    pageNumber += 1;
  }
  return out;
}

/** API/행 공통 — 우측 유형 칩: 개인·기업·운영자 등 (분야·지역은 표시하지 않음) */
function proposalTargetMemberTypeLabel(u) {
  if (!u || typeof u !== 'object') return '회원';
  const cd = String(u.roleCd || u.authorCd || u.userRoleCd || '').toUpperCase();
  if (cd === 'INDIVIDUAL') return '개인';
  if (cd === 'COMPANY') return '기업';
  if (cd === 'ADMIN') return '운영자';
  const nm = String(u.roleNm || u.authorNm || u.type || '').trim();
  if (nm.includes('기업') || nm.toUpperCase().includes('COMPANY')) return '기업';
  if (nm.includes('개인') || nm.toUpperCase().includes('INDIVIDUAL')) return '개인';
  if (nm.includes('관리자') || nm.toUpperCase().includes('ADMIN')) return '운영자';
  if (nm === '전문가') return '개인';
  if (nm === '기관') return '기업';
  return nm || '회원';
}

/** config.items·seedRows 등 레거시 행 → 유형 라벨 */
function memberTypeLabelFromItemRow(row) {
  if (!row || typeof row !== 'object') return '회원';
  if (row.memberTypeLabel) return String(row.memberTypeLabel).trim() || '회원';
  return proposalTargetMemberTypeLabel(row);
}

function mapProposalTargetUserToItem(u) {
  if (!u || u.uid == null) return null;
  const label = proposalTargetMemberTypeLabel(u);
  return {
    id: u.uid,
    name: u.userNm || u.userId || u.userEmail || '(이름 없음)',
    userId: u.userId || '',
    authorCd: u.roleCd || u.authorCd || '',
    memberTypeLabel: label,
    type: label,
    field: u.deptNm || u.workplaceNm || '',
    region: u.farmNm || '',
    email: u.userEmail || '',
    skipUserFetch: false,
  };
}

function mapTaskRowFromSource(kind, row) {
  // 연구(/api/user-rnd-interests/my): row.uid 는 GLOW_USER_RND_INTEREST 의 PK이고,
  // 협업 제안·검증에 쓰이는 공고 키는 rndAnnouncementUid 이다. uid 를 쓰면 400(관심 미등록)으로 이어진다.
  // 기술수요(/api/tech-demands/my)·커뮤니티(/api/community-projects/my): row.uid 가 각각 기술수요·프로젝트 PK이므로 uid 우선이 맞다.
  const uidRaw =
    kind === TASK_SOURCE_RESEARCH
      ? (row?.rndAnnouncementUid ?? row?.no ?? row?.demandId)
      : (row?.uid ?? row?.demandId ?? row?.rndAnnouncementUid ?? row?.no);
  if (uidRaw == null || uidRaw === '') return null;
  const demandId = Number(uidRaw);
  if (!Number.isFinite(demandId)) return null;
  const title = String(row?.title || row?.projectName || row?.rndTitle || '').trim()
    || (kind === TASK_SOURCE_RESEARCH ? `연구사업 #${uidRaw}` : `공고 #${uidRaw}`);
  const deadline = row?.deadlineAt || row?.deadline || row?.appDue || '';
  const budget = row?.budget || row?.amount || '';
  return { demandId, title, deadline, budget, kind };
}

function buildTaskDetailPath(kind, demandId) {
  if (kind === TASK_SOURCE_RESEARCH) return `/research/pre-review-detail/${demandId}/applicants`;
  if (kind === TASK_SOURCE_COMMUNITY) return buildProjectDetailPath('community', demandId);
  return buildProjectDetailPath('demand', demandId);
}

/** CardListE NTIS·국가연구사업 상세와 동일: 지정 크기 새 창 */
const TASK_DETAIL_POPUP_NAME = 'cardlist_e_title_popup';
const TASK_DETAIL_POPUP_FEATURES = 'width=1400,height=800,resizable=yes,scrollbars=yes';

function openNtisStyleTaskDetailWindow(url) {
  if (!url) return;
  const w = window.open(url, TASK_DETAIL_POPUP_NAME, TASK_DETAIL_POPUP_FEATURES);
  if (w) w.focus();
}

/** 부모 화면 기준 과제 소스 고정 시 — 라디오 숨김 */
function normalizeProposalTaskSource(raw) {
  const s = String(raw || '').toLowerCase().trim();
  if (s === TASK_SOURCE_TECH_DEMAND || s === 'demand') return TASK_SOURCE_TECH_DEMAND;
  if (s === TASK_SOURCE_RESEARCH || s === 'rnd') return TASK_SOURCE_RESEARCH;
  if (s === TASK_SOURCE_COMMUNITY) return TASK_SOURCE_COMMUNITY;
  return null;
}

const SIMPLE_EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** 대상 사용자 중 GLOW 플랫폼 관리자(ADMIN · SYSTEM) 권한 여부 */
const isGlowAdminTarget = (u) => {
  if (isGlowPlatformAdmin(u)) return true;
  const roleCandidates = [
    u?.userRoleCd,
    u?.authorCd,
    u?.roleCd,
    u?.roleNm,
    u?.type,
    u?.authorNm,
  ]
    .filter(Boolean)
    .map((v) => String(v).trim().toUpperCase());
  const joined = roleCandidates.join(' ');
  return roleCandidates.includes('ADMIN')
    || roleCandidates.includes('SYSTEM')
    || joined.includes('GLOW 관리자'.toUpperCase())
    || joined.includes('관리자');
};

/**
 * 전문가 / 기관 정보 팝업 (다중선택 + 메시지 제안)
 * config.items: { id, name, type, field, region?, desc? }[]
 * config.initialSelectedIds: number[]
 * config.senderName: string (From. 표시명 fallback)
 * config.senderRegUid | config.senderUid: 보낸사람 uid (AuthorDisplay 조회)
 * config.senderUserId, config.senderAuthorCd, config.senderOrg, config.senderSkipUserFetch (선택)
 * config.taskTitle: string (과제 정보 제목 등)
 * config.demandId: number|string (기술 수요 상세 이동용, 있으면 상단 과제 정보 + 상세보기 노출)
 * config.detailPath: string (상세보기 URL, demandId 없을 때. 클릭 시 CardListE·NTIS와 동일 크기 팝업 창으로 열림)
 * config.getDetailPath: (task) => string (taskOptions 사용 시 과제 상세보기 경로. 미제공 시 /demand/tech-demand-detail/:id)
 * config.taskInfo: { title?, deadline?, budget?, projectName? } (과제/연구사업 추가 정보)
 * config.taskOptions: { demandId, title, deadline?, budget? }[] (제공 시 상단에 '원하는 과제 선택' 드롭다운 노출, 선택한 과제로 제안)
 * config.hideExpertOrgList: boolean (true 시 좌측 기관/전문가 영역 숨김, 우측 과제정보 영역만 전체 너비로 표시)
 * config.proposalTargetsPaging: { kind: 'tech-demand' | 'community' | 'research', demandId, excludeUserId?, seedRows? } — 제공 시 좌측 목록은 이름 검색어를 입력한 뒤에만 API 조회·서버 페이지네이션(size=5), 초기에는 목록 비움. items 미사용. research일 때 demandId=RND 공고 UID, API는 rndAnnouncementUid로 전달. seedRows: 초기 선택용 { id,name,type,field,region?,email? }[]
 * config.proposalTaskSource: 'tech-demand' | 'research' | 'community' — enableTaskSourceSelector 와 함께 쓰면 기술수요/연구/커뮤니티 라디오를 숨기고 해당 소스만 조회
 */
const ExpertOrgSelectPopup = ({ config, onClose }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const taskSourceSelectorEnabled = config?.enableTaskSourceSelector === true;
  const fixedProposalTaskSource = useMemo(
    () => normalizeProposalTaskSource(config?.proposalTaskSource ?? config?.initialTaskSourceKind),
    [config?.proposalTaskSource, config?.initialTaskSourceKind],
  );
  const [taskSourceKind, setTaskSourceKind] = useState(
    () => normalizeProposalTaskSource(config?.proposalTaskSource ?? config?.initialTaskSourceKind) || TASK_SOURCE_TECH_DEMAND,
  );
  useEffect(() => {
    const next = normalizeProposalTaskSource(config?.proposalTaskSource ?? config?.initialTaskSourceKind);
    if (next) setTaskSourceKind(next);
  }, [config?.proposalTaskSource, config?.initialTaskSourceKind]);
  const effectiveTaskSourceKind = fixedProposalTaskSource ?? taskSourceKind;
  const [taskSearchKeyword, setTaskSearchKeyword] = useState('');
  const [taskDebouncedKeyword, setTaskDebouncedKeyword] = useState('');
  const [taskPageIndex, setTaskPageIndex] = useState(0);
  const [taskRows, setTaskRows] = useState([]);
  const [taskTotalPages, setTaskTotalPages] = useState(1);
  const [taskLoading, setTaskLoading] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setTaskDebouncedKeyword(String(taskSearchKeyword || '').trim()), 300);
    return () => clearTimeout(t);
  }, [taskSearchKeyword]);

  const fetchTargets = useCallback(async () => {
    try {
      const { demandId } = config || {};
      const baseParams = {};
      if (demandId) baseParams.rndAnnouncementUid = demandId;

      const rawList = await fetchAllRndCollaborationTargetsMerged(
        (cfg) => api.get('/api/research/collaboration-proposals/targets', cfg),
        baseParams
      );
      const list = rawList.filter((u) => !isGlowAdminTarget(u));
      const mapped = list
        .map((u) => mapProposalTargetUserToItem(u))
        .filter(Boolean);
      setItems(mapped.length > 0 ? mapped : DEFAULT_EXPERT_ORG_ITEMS);
    } catch (e) {
      console.error('Failed to load targets:', e);
      setItems(DEFAULT_EXPERT_ORG_ITEMS);
    }
  }, [config]);

  const pagingCfg = config?.proposalTargetsPaging;
  const useServerProposalTargets = Boolean(
    taskSourceSelectorEnabled
    || (
      pagingCfg
      && pagingCfg.demandId != null
      && String(pagingCfg.demandId).trim() !== ''
      && (pagingCfg.kind === 'tech-demand' || pagingCfg.kind === 'community' || pagingCfg.kind === 'research')
    )
  );

  useEffect(() => {
    if (Array.isArray(config?.items) && config.items.length > 0) {
      setItems(
        config.items
          .filter((u) => !isGlowAdminTarget(u))
          .map((row) => {
            const id = row.id ?? row.uid;
            const label = memberTypeLabelFromItemRow(row);
            return {
              ...row,
              id,
              userId: row.userId || '',
              authorCd: row.authorCd || row.roleCd || '',
              memberTypeLabel: label,
              type: label,
              skipUserFetch: Boolean(row.skipUserFetch),
            };
          })
      );
      return;
    }
    if (useServerProposalTargets) {
      setItems([]);
      return;
    }
    fetchTargets();
  }, [config?.items, fetchTargets, useServerProposalTargets]);

  const taskOptions = useMemo(() => {
    if (taskSourceSelectorEnabled) return taskRows;
    return Array.isArray(config?.taskOptions) && config.taskOptions.length > 0 ? config.taskOptions : null;
  }, [config?.taskOptions, taskRows, taskSourceSelectorEnabled]);

  const initialTaskId = taskOptions?.[0]?.demandId != null ? String(taskOptions[0].demandId) : '';
  const [selectedTaskId, setSelectedTaskId] = useState(initialTaskId);

  const selectedTask = useMemo(
    () => (taskOptions && selectedTaskId ? taskOptions.find((t) => String(t.demandId) === String(selectedTaskId)) : null),
    [taskOptions, selectedTaskId]
  );
  const effectiveProposalKind = taskSourceSelectorEnabled ? effectiveTaskSourceKind : pagingCfg?.kind;
  const effectiveProposalDemandId = taskSourceSelectorEnabled ? selectedTask?.demandId : pagingCfg?.demandId;

  useEffect(() => {
    if (!taskSourceSelectorEnabled) return;
    setTaskPageIndex(0);
    setSelectedTaskId('');
  }, [effectiveTaskSourceKind, taskDebouncedKeyword, taskSourceSelectorEnabled]);

  useEffect(() => {
    if (!taskSourceSelectorEnabled) return undefined;
    let cancelled = false;
    (async () => {
      setTaskLoading(true);
      try {
        const params = {
          pageNumber: taskPageIndex + 1,
          size: TASK_LIST_PAGE_SIZE,
        };
        if (taskDebouncedKeyword) params.searchKeyword = taskDebouncedKeyword;
        let res = null;
        if (effectiveTaskSourceKind === TASK_SOURCE_RESEARCH) {
          res = await api.get('/api/user-rnd-interests/my', { params });
        } else if (effectiveTaskSourceKind === TASK_SOURCE_COMMUNITY) {
          res = await api.get('/api/community-projects/my', { params });
        } else {
          res = await api.get('/api/tech-demands/my', { params });
        }
        if (cancelled) return;
        const raw = Array.isArray(res?.list) ? res.list : Array.isArray(res?.content) ? res.content : [];
        const mapped = raw.map((r) => mapTaskRowFromSource(effectiveTaskSourceKind, r)).filter(Boolean);
        setTaskRows(mapped);
        const tp = Number(res?.totalPages);
        const te = Number(res?.totalElements);
        const calc = Number.isFinite(tp) && tp >= 1
          ? tp
          : Math.max(1, Math.ceil((Number.isFinite(te) ? te : mapped.length) / TASK_LIST_PAGE_SIZE));
        setTaskTotalPages(calc);
      } catch (e) {
        if (!cancelled) {
          console.error('[ExpertOrgSelectPopup] task list page', e);
          setTaskRows([]);
          setTaskTotalPages(1);
        }
      } finally {
        if (!cancelled) setTaskLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [taskSourceSelectorEnabled, effectiveTaskSourceKind, taskPageIndex, taskDebouncedKeyword]);

  const normalizedInitialSelectedIds = useMemo(
    () => (config?.initialSelectedIds || []).filter((v) => v != null && String(v).trim() !== ''),
    [config?.initialSelectedIds]
  );
  const initialIds = useMemo(() => new Set(normalizedInitialSelectedIds), [normalizedInitialSelectedIds]);
  const [selectedIds, setSelectedIds] = useState(() => new Set(initialIds));
  const [messageBody, setMessageBody] = useState(
    config?.defaultMessage ?? t('popup.expertOrgSelect.defaultMessage')
  );
  const [sendMode, setSendMode] = useState('PUSH');
  const [recipientInputTab, setRecipientInputTab] = useState('MEMBER');
  const [guestEmailInput, setGuestEmailInput] = useState('');
  const [guestEmails, setGuestEmails] = useState([]);

  const showOnlySelected = config?.showOnlySelected === true && initialIds.size > 0;
  const hideExpertOrgList = config?.hideExpertOrgList === true;

  const [expertOrgSearchKeyword, setExpertOrgSearchKeyword] = useState('');
  const ITEMS_PER_PAGE = 5;
  const [currentPage, setCurrentPage] = useState(0);

  const [debouncedUserNm, setDebouncedUserNm] = useState('');
  const [serverPageRows, setServerPageRows] = useState([]);
  const [serverTotalPages, setServerTotalPages] = useState(1);
  const [serverLoading, setServerLoading] = useState(false);
  const [targetRowById, setTargetRowById] = useState({});

  useEffect(() => {
    const t = setTimeout(() => setDebouncedUserNm(String(expertOrgSearchKeyword || '').trim()), 300);
    return () => clearTimeout(t);
  }, [expertOrgSearchKeyword]);

  useEffect(() => {
    if (!useServerProposalTargets) return;
    setCurrentPage(0);
    const seeds = Array.isArray(pagingCfg?.seedRows) ? pagingCfg.seedRows : [];
    const seededMap = {};
    seeds.forEach((r) => {
      const id = r.id ?? r.uid;
      if (id == null) return;
      const label = memberTypeLabelFromItemRow(r);
      seededMap[id] = {
        id: Number(id),
        name: r.name || r.email || r.userEmail || '(이름 없음)',
        userId: r.userId || '',
        authorCd: r.authorCd || r.roleCd || '',
        memberTypeLabel: label,
        type: label,
        field: r.field || r.deptNm || '',
        region: r.region || r.farmNm || '',
        email: r.email || r.userEmail || '',
        skipUserFetch: Boolean(r.skipUserFetch),
      };
    });
    setTargetRowById(seededMap);
    setExpertOrgSearchKeyword('');
    setDebouncedUserNm('');
    setSelectedIds(new Set(normalizedInitialSelectedIds));
  }, [useServerProposalTargets, effectiveProposalDemandId, effectiveProposalKind, pagingCfg?.seedRows, normalizedInitialSelectedIds]);

  useEffect(() => {
    if (useServerProposalTargets) {
      setCurrentPage(0);
    }
  }, [debouncedUserNm, useServerProposalTargets]);

  const proposalSearchQuery = useMemo(
    () => String(debouncedUserNm || '').trim(),
    [debouncedUserNm]
  );

  useEffect(() => {
    if (!useServerProposalTargets || !effectiveProposalKind || !effectiveProposalDemandId) return undefined;
    if (!proposalSearchQuery) {
      setServerPageRows([]);
      setServerTotalPages(1);
      setServerLoading(false);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      setServerLoading(true);
      try {
        let url = '/api/tech-demands/proposals/targets';
        const params = {
          pageNumber: currentPage + 1,
          size: PROPOSAL_TARGETS_PAGE_SIZE,
        };
        params.userNm = proposalSearchQuery;
        if (effectiveProposalKind === 'community') {
          url = '/api/community-projects/proposals/targets';
          params.demandId = effectiveProposalDemandId;
        } else if (effectiveProposalKind === 'research') {
          url = '/api/research/collaboration-proposals/targets';
          params.rndAnnouncementUid = effectiveProposalDemandId;
        } else {
          params.demandId = effectiveProposalDemandId;
        }
        if (pagingCfg?.excludeUserId) params.excludeUserId = pagingCfg.excludeUserId;
        const res = await api.get(url, { params });
        if (cancelled) return;
        const raw = res?.list ?? res?.content ?? [];
        const mapped = raw
          .filter((u) => !isGlowAdminTarget(u))
          .map((u) => mapProposalTargetUserToItem(u))
          .filter(Boolean);
        setServerPageRows(mapped);
        const te = Number(res?.totalElements);
        const tp = Number(res?.totalPages);
        const safeTe = Number.isFinite(te) ? te : mapped.length;
        setServerTotalPages(
          Number.isFinite(tp) && tp >= 1
            ? tp
            : Math.max(1, Math.ceil(safeTe / PROPOSAL_TARGETS_PAGE_SIZE) || 1)
        );
        setTargetRowById((prev) => {
          const next = { ...prev };
          mapped.forEach((r) => {
            next[r.id] = r;
          });
          return next;
        });
      } catch (e) {
        if (!cancelled) {
          console.error('[ExpertOrgSelectPopup] proposal targets page', e);
          setServerPageRows([]);
          setServerTotalPages(1);
        }
      } finally {
        if (!cancelled) setServerLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [useServerProposalTargets, effectiveProposalKind, effectiveProposalDemandId, pagingCfg?.excludeUserId, currentPage, proposalSearchQuery]);

  const filteredItems = useMemo(() => {
    if (showOnlySelected) {
      return items.filter((i) => initialIds.has(i.id));
    }
    const k = String(expertOrgSearchKeyword || '').trim().toLowerCase();
    if (!k) return items;
    return items.filter(
      (i) =>
        (i.name || '').toLowerCase().includes(k) ||
        (i.type || '').toLowerCase().includes(k) ||
        (i.field || '').toLowerCase().includes(k) ||
        (i.region || '').toLowerCase().includes(k) ||
        (i.email || '').toLowerCase().includes(k)
    );
  }, [items, expertOrgSearchKeyword, showOnlySelected, initialIds]);

  // 클라이언트 목록: 검색어 변경 시 1페이지
  useEffect(() => {
    if (useServerProposalTargets) return;
    setCurrentPage(0);
  }, [expertOrgSearchKeyword, useServerProposalTargets]);

  const totalPages = Math.max(1, Math.ceil(filteredItems.length / ITEMS_PER_PAGE));
  const safePage = Math.min(currentPage, Math.max(0, totalPages - 1));

  useEffect(() => {
    if (useServerProposalTargets) return;
    if (totalPages > 0 && currentPage >= totalPages) {
      setCurrentPage(totalPages - 1);
    }
  }, [totalPages, currentPage, useServerProposalTargets]);

  const listTotalPages = useServerProposalTargets ? Math.max(1, serverTotalPages) : totalPages;
  useEffect(() => {
    if (!useServerProposalTargets) return;
    if (listTotalPages > 0 && currentPage >= listTotalPages) {
      setCurrentPage(Math.max(0, listTotalPages - 1));
    }
  }, [listTotalPages, currentPage, useServerProposalTargets]);

  const listSafePage = Math.min(currentPage, Math.max(0, listTotalPages - 1));

  const paginatedItems = useMemo(() => {
    const start = safePage * ITEMS_PER_PAGE;
    return filteredItems.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredItems, safePage, ITEMS_PER_PAGE]);

  const listRows = useServerProposalTargets ? serverPageRows : paginatedItems;

  const selectedItems = useMemo(() => {
    const out = [];
    selectedIds.forEach((id) => {
      const row = targetRowById[id] ?? items.find((i) => String(i.id) === String(id));
      if (row) out.push(row);
    });
    return out;
  }, [selectedIds, targetRowById, items]);
  const hasSelection = selectedItems.length > 0 || guestEmails.length > 0;

  const toggle = useCallback((id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const handleAddGuestEmail = useCallback(async () => {
    const t = String(guestEmailInput || '').trim().toLowerCase();
    if (!SIMPLE_EMAIL_RE.test(t)) {
      window.alert(t('popup.expertOrgSelect.alertInvalidEmail'));
      return;
    }
    const myEmail = String(user?.userEmail || '').trim().toLowerCase();
    if (myEmail && t === myEmail) {
      window.alert(t('popup.expertOrgSelect.alertSelfEmailNotAllowed'));
      return;
    }
    try {
      const brief = await api.get('/api/glow/members/lookup-by-email', { params: { email: t } });
      if (brief?.uid) {
        const uidStr = String(brief.uid);
        if (selectedIds.has(uidStr)) {
          window.alert(t('popup.expertOrgSelect.alertAlreadyRecipient'));
          setGuestEmailInput('');
          return;
        }
        setSelectedIds((prev) => new Set([...prev, uidStr]));
        setTargetRowById((prev) => ({
          ...prev,
          [uidStr]: {
            id: brief.uid,
            userId: brief.userId || '',
            name: brief.userNm || '',
            authorCd: brief.authorCd || '',
          },
        }));
        setGuestEmailInput('');
        return;
      }
    } catch {
      /* 404: 비회원 이메일로 계속 */
    }
    setGuestEmails((prev) => (prev.includes(t) ? prev : [...prev, t]));
    setGuestEmailInput('');
  }, [guestEmailInput, user?.userEmail, selectedIds]);

  const removeGuestEmail = useCallback((email) => {
    setGuestEmails((prev) => prev.filter((v) => v !== email));
  }, []);

  const handleConfirm = useCallback(() => {
    const myEmail = String(user?.userEmail || '').trim().toLowerCase();
    if (myEmail && guestEmails.some((e) => String(e).trim().toLowerCase() === myEmail)) {
      window.alert(t('popup.expertOrgSelect.alertSelfEmailNotAllowed'));
      return;
    }
    const recipients = [
      ...selectedItems.map((item) => ({ memberUid: Number(item.id) })),
      ...guestEmails.map((email) => ({ guestEmail: email })),
    ];
    if (!recipients.length) {
      window.alert(t('popup.expertOrgSelect.alertAddRecipient'));
      return;
    }
    onClose(true, {
      selected: selectedItems,
      recipients,
      guestEmails,
      sendMode,
      messageBody,
      notifyPlatform: sendMode === 'PUSH',
      notifyEmail: sendMode === 'EMAIL',
      ...(selectedTask ? { demandId: selectedTask.demandId, taskTitle: selectedTask.title, proposalKind: selectedTask.kind || effectiveProposalKind } : {}),
    });
  }, [selectedItems, guestEmails, sendMode, messageBody, selectedTask, onClose, effectiveProposalKind, user?.userEmail, t]);

  const handleCancel = useCallback(() => {
    onClose(false);
  }, [onClose]);

  const fromRegUid = config?.senderRegUid ?? config?.senderUid ?? user?.uid;
  const fromDisplayName = config?.senderName ?? user?.userNm ?? user?.userName ?? t('popup.expertOrgSelect.me');
  const fromUserId = config?.senderUserId ?? user?.userId ?? '';
  const fromAuthorCd = config?.senderAuthorCd ?? user?.authorCd ?? '';
  const fromOrg = config?.senderOrg ?? '';
  const fromSkipUserFetch =
    config?.senderSkipUserFetch === true || fromRegUid == null || fromRegUid === '';

  const showTaskSelector = taskSourceSelectorEnabled || (taskOptions != null && taskOptions.length > 0);
  const showTaskInfo =
    showTaskSelector && selectedTask
      ? true
      : (config?.taskTitle != null || config?.demandId != null);
  const taskTitle = selectedTask?.title ?? config?.taskTitle ?? (config?.demandId != null ? `공고 #${config.demandId}` : '');
  const taskInfo = selectedTask
    ? { deadline: selectedTask.deadline, budget: selectedTask.budget, projectName: selectedTask.title }
    : config?.taskInfo ?? null;
  const effectiveDemandId = selectedTask?.demandId ?? config?.demandId;
  const detailHref = config?.detailHref;

  const taskDetailOpenUrl = useMemo(() => {
    if (detailHref != null && String(detailHref).trim() !== '') {
      return String(detailHref).trim();
    }
    let path = null;
    if (typeof config?.getDetailPath === 'function' && selectedTask) {
      path = config.getDetailPath(selectedTask);
    } else if (config?.detailPath != null && String(config.detailPath).trim() !== '') {
      path = config.detailPath;
    } else if (effectiveDemandId != null) {
      path = buildTaskDetailPath(selectedTask?.kind || effectiveProposalKind, effectiveDemandId);
    }
    if (path == null || String(path).trim() === '') return null;
    const p = String(path).trim();
    if (/^https?:\/\//i.test(p)) return p;
    const normalized = p.startsWith('/') ? p : `/${p}`;
    return `${window.location.origin}${normalized}`;
  }, [detailHref, config, selectedTask, effectiveDemandId, effectiveProposalKind]);

  const hasViewDetail = taskDetailOpenUrl != null;

  const handleViewDetail = useCallback(() => {
    openNtisStyleTaskDetailWindow(taskDetailOpenUrl);
  }, [taskDetailOpenUrl]);

  const showExpertList = !showOnlySelected && !hideExpertOrgList;
  const messageInTopSplit = showTaskSelector;
  const isGuestEmailInputMode = sendMode === 'EMAIL' && recipientInputTab === 'GUEST';

  const proposalMessageColumn = (
    <>
      <div className="expert-org-select-task-box">
        <div className="expert-org-select-task-title">{t('popup.expertOrgSelect.taskInfoTitle')}</div>
        {showTaskInfo && (taskOptions ? selectedTask : true) && (
          <div className="expert-org-select-task-info-in-box">
            <div className="expert-org-select-task-info-title-row">
              <div className="expert-org-select-task-info-title">{taskTitle}</div>
              {hasViewDetail ? (
                <button type="button" className="expert-org-select-view-detail-btn" onClick={handleViewDetail}>
                  {t('popup.expertOrgSelect.viewDetail')}
                </button>
              ) : null}
            </div>
            <div className="expert-org-select-task-info-meta-line">
              {[
                taskInfo?.deadline != null && `${t('popup.expertOrgSelect.deadlineLabel')}: ${taskInfo.deadline}`,
                (taskInfo?.budget != null || taskInfo?.amount != null) && `${t('popup.expertOrgSelect.budgetLabel')}: ${taskInfo.budget ?? taskInfo.amount}`,
              ]
                .filter(Boolean)
                .join(' · ')}
            </div>
          </div>
        )}
        <div className="expert-org-select-field-row expert-org-select-field-row--author">
          <span className="expert-org-select-field-label">{t('popup.expertOrgSelect.toLabel')}</span>
          <div className="expert-org-select-field-value expert-org-select-field-value--authors">
            {hasSelection ? (
              <>
                {selectedItems.map((item) => (
                  <AuthorDisplay
                    key={item.id}
                    regUid={item.skipUserFetch ? undefined : item.id}
                    name={item.name || ''}
                    userId={item.userId || ''}
                    authorCd={item.authorCd || ''}
                    skipUserFetch={item.skipUserFetch === true}
                    className="expert-org-select-author-display"
                  />
                ))}
                {guestEmails.map((email) => (
                  <span key={email} className="expert-org-select-guest-chip" title={email}>
                    <span className="expert-org-select-guest-email">{email}</span>
                    <button
                      type="button"
                      className="expert-org-select-guest-remove"
                      onClick={() => removeGuestEmail(email)}
                      aria-label={t('popup.expertOrgSelect.removeEmailAria', { email })}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </>
            ) : (
              <span className="expert-org-select-field-placeholder">{t('popup.expertOrgSelect.selectRecipientPlaceholder')}</span>
            )}
          </div>
        </div>
        <div className="expert-org-select-message-row">
          <textarea
            className="expert-org-select-message-input"
            value={messageBody}
            onChange={(e) => setMessageBody(e.target.value)}
            placeholder={t('popup.expertOrgSelect.messagePlaceholder')}
            rows={4}
            disabled={!hasSelection}
          />
        </div>
        <div className="expert-org-select-field-row expert-org-select-field-row--author">
          <span className="expert-org-select-field-label">{t('popup.expertOrgSelect.fromLabel')}</span>
          <div className="expert-org-select-field-value expert-org-select-field-value--authors">
            <AuthorDisplay
              regUid={fromSkipUserFetch ? undefined : fromRegUid}
              name={fromDisplayName}
              userId={fromUserId}
              authorCd={fromAuthorCd}
              org={fromOrg}
              skipUserFetch={fromSkipUserFetch}
              className="expert-org-select-author-display"
            />
          </div>
        </div>
      </div>
    </>
  );

  return (
    <PopupWrapper
      config={config}
      onClose={onClose}
      displayType={config?.displayType || 'modal'}
      className="popup-expert-org-select"
    >
      <div className="main-popup-body popup-expert-org-select-body">
        <div className="expert-org-select-send-mode-tabs-wrap">
          <Tabs
            tabs={[t('popup.expertOrgSelect.pushTab'), t('popup.expertOrgSelect.emailTab')]}
            activeTab={sendMode === 'EMAIL' ? 1 : 0}
            onChange={(tabIndex) => setSendMode(tabIndex === 1 ? 'EMAIL' : 'PUSH')}
            variant="pill"
            className="expert-org-select-send-mode-tabs"
            activeColor="#4f46e5"
          />
        </div>
        {showTaskSelector ? (
          <div className="row g-2 expert-org-select-proposal-split">
            <div className="col-6 expert-org-select-proposal-split-left d-flex flex-column">
              <div className="expert-org-select-task-selector-top">
                <label className="expert-org-select-task-selector-label">{t('popup.expertOrgSelect.selectTaskLabel')}</label>
                {taskSourceSelectorEnabled ? (
                  <>
                    {!fixedProposalTaskSource ? (
                      <div className="expert-org-select-source-radios" role="radiogroup" aria-label={t('popup.expertOrgSelect.taskCategoryAria')}>
                        <label className="expert-org-select-source-radio"><input type="radio" name="proposal-source-kind" checked={taskSourceKind === TASK_SOURCE_TECH_DEMAND} onChange={() => setTaskSourceKind(TASK_SOURCE_TECH_DEMAND)} /><span>{t('popup.expertOrgSelect.taskSourceTechDemand')}</span></label>
                        <label className="expert-org-select-source-radio"><input type="radio" name="proposal-source-kind" checked={taskSourceKind === TASK_SOURCE_RESEARCH} onChange={() => setTaskSourceKind(TASK_SOURCE_RESEARCH)} /><span>{t('popup.expertOrgSelect.taskSourceResearch')}</span></label>
                        <label className="expert-org-select-source-radio"><input type="radio" name="proposal-source-kind" checked={taskSourceKind === TASK_SOURCE_COMMUNITY} onChange={() => setTaskSourceKind(TASK_SOURCE_COMMUNITY)} /><span>{t('popup.expertOrgSelect.taskSourceCommunity')}</span></label>
                      </div>
                    ) : null}
                    <input
                      type="search"
                      className="form-control expert-org-select-task-search-input expert-org-select-task-combo"
                      placeholder={t('popup.expertOrgSelect.searchTaskPlaceholder')}
                      value={taskSearchKeyword}
                      onChange={(e) => setTaskSearchKeyword(e.target.value)}
                      autoComplete="off"
                    />
                    <div className="expert-org-select-task-list-panel">
                      <ul className="expert-org-select-task-list">
                        {taskLoading && taskRows.length === 0 ? <li className="expert-org-select-empty">{t('popup.common.processing')}</li> : null}
                        {!taskLoading && taskRows.length === 0 ? (
                          <li className="expert-org-select-empty">
                            {effectiveTaskSourceKind === TASK_SOURCE_RESEARCH
                              ? t('popup.expertOrgSelect.emptyResearchTasks')
                              : effectiveTaskSourceKind === TASK_SOURCE_COMMUNITY
                                ? t('popup.expertOrgSelect.emptyCommunityTasks')
                                : t('popup.expertOrgSelect.emptyTechDemandTasks')}
                          </li>
                        ) : null}
                        {taskRows.map((t) => (
                          <li key={`${t.kind}-${t.demandId}`} className="expert-org-select-task-list-item">
                            <label className="expert-org-select-task-list-row">
                              <input
                                type="radio"
                                name="proposal-task-id"
                                checked={String(selectedTaskId) === String(t.demandId)}
                                onChange={() => setSelectedTaskId(String(t.demandId))}
                              />
                              <span className="expert-org-select-task-list-title">{t.title}</span>
                            </label>
                          </li>
                        ))}
                      </ul>
                      <div className="expert-org-select-task-pagination-slot">
                        {taskTotalPages > 1 ? (
                          <div className="expert-org-select-task-pagination">
                            <Pagination currentPage={taskPageIndex} totalPages={taskTotalPages} onPageChange={setTaskPageIndex} />
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </>
                ) : (
                  <AutocompleteCombo
                    options={taskOptions}
                    getOptionLabel={(t) => t.title || `공고 #${t.demandId}`}
                    getOptionValue={(t) => String(t.demandId)}
                    value={selectedTaskId || ''}
                    onChange={(val) => setSelectedTaskId(val || '')}
                    placeholder={t('popup.expertOrgSelect.searchOrSelectTaskPlaceholder')}
                    inputId="expert-org-select-task-combo"
                    aria-label={t('popup.expertOrgSelect.taskSearchAria')}
                    className="expert-org-select-task-combo"
                  />
                )}
              </div>
              {!selectedTask ? (
                <p className="expert-org-select-task-selector-hint">{t('popup.expertOrgSelect.taskSelectorHint')}</p>
              ) : null}
            </div>
            <div className="col-6 expert-org-select-proposal-split-right d-flex flex-column">{proposalMessageColumn}</div>
          </div>
        ) : null}
        {(showExpertList || !messageInTopSplit) && (
        <div
          className={`row g-3 align-items-stretch expert-org-select-layout${showOnlySelected || hideExpertOrgList ? ' expert-org-select-layout--only-selected' : ''}`}
        >
          {/* 왼쪽: 기관/전문가 조회 (원하는 과제 선택 섹션 아래) — showOnlySelected 또는 hideExpertOrgList 시 숨김 */}
          {!showOnlySelected && !hideExpertOrgList && (
            <div
              className={`expert-org-select-list-area d-flex flex-column min-w-0 ${
                showTaskSelector && showExpertList ? 'col-12' : 'col-6'
              }`}
            >
              <div className="expert-org-select-list-filter">
                {sendMode === 'EMAIL' ? (
                  <div className="expert-org-select-recipient-tabs-wrap">
                    <Tabs
                      tabs={[t('popup.expertOrgSelect.memberSearchTab'), t('popup.expertOrgSelect.emailInputTab')]}
                      activeTab={recipientInputTab === 'GUEST' ? 1 : 0}
                      onChange={(tabIndex) => setRecipientInputTab(tabIndex === 1 ? 'GUEST' : 'MEMBER')}
                      variant="segmented"
                      className="expert-org-select-recipient-tabs"
                    />
                  </div>
                ) : null}
                {isGuestEmailInputMode ? (
                  <div className="expert-org-select-guest-input-row">
                    <input
                      type="email"
                      className="form-control form-control-sm expert-org-select-guest-input"
                      placeholder={t('popup.expertOrgSelect.guestEmailPlaceholder')}
                      value={guestEmailInput}
                      onChange={(e) => setGuestEmailInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleAddGuestEmail();
                        }
                      }}
                    />
                    <button type="button" className="btn btn-sm expert-org-select-guest-add-btn" onClick={handleAddGuestEmail} aria-label={t('popup.expertOrgSelect.addEmailAria')}>
                      +
                    </button>
                  </div>
                ) : null}
                {!isGuestEmailInputMode && useServerProposalTargets ? (
                  <>
                    <label className="visually-hidden" htmlFor="expert-org-select-server-search">
                      {t('popup.expertOrgSelect.searchNameLabel')}
                    </label>
                    <input
                      id="expert-org-select-server-search"
                      type="search"
                      className="form-control expert-org-select-list-combo"
                      placeholder={t('popup.expertOrgSelect.searchByNamePlaceholder')}
                      value={expertOrgSearchKeyword}
                      onChange={(e) => setExpertOrgSearchKeyword(e.target.value)}
                      autoComplete="off"
                      spellCheck={false}
                    />
                  </>
                ) : !isGuestEmailInputMode ? (
                  <AutocompleteCombo
                    options={items}
                    getOptionLabel={(i) => i.name}
                    getOptionValue={(i) => i.id}
                    inputValue={expertOrgSearchKeyword}
                    onInputChange={setExpertOrgSearchKeyword}
                    onSelectItem={(item) => {
                      toggle(item.id);
                      setExpertOrgSearchKeyword('');
                    }}
                    placeholder={t('popup.expertOrgSelect.searchMemberPlaceholder')}
                    filterOption={(item, inputValue) => {
                      const k = String(inputValue || '').trim().toLowerCase();
                      if (!k) return true;
                      return (
                        (item.name || '').toLowerCase().includes(k) ||
                        (item.type || '').toLowerCase().includes(k) ||
                        (item.field || '').toLowerCase().includes(k) ||
                        (item.region || '').toLowerCase().includes(k) ||
                        (item.email || '').toLowerCase().includes(k)
                      );
                    }}
                    inputId="expert-org-select-list-combo"
                    aria-label={t('popup.expertOrgSelect.memberSearchAria')}
                    className="expert-org-select-list-combo"
                  />
                ) : null}
              </div>
              {!isGuestEmailInputMode ? (
                <>
                  <div className="expert-org-select-toolbar">
                    <span className="expert-org-select-hint expert-org-select-hint--only">
                      {useServerProposalTargets && !effectiveProposalDemandId
                        ? t('popup.expertOrgSelect.selectTaskFirstHint')
                        : t('popup.expertOrgSelect.selectionCount', {
                            members: selectedIds.size,
                            guests: guestEmails.length,
                          })}
                      {useServerProposalTargets && proposalSearchQuery && serverLoading ? ` · ${t('popup.common.processing')}` : ''}
                    </span>
                  </div>
                  <ul className="expert-org-select-list">
                    {useServerProposalTargets && !effectiveProposalDemandId ? (
                      <li className="expert-org-select-empty expert-org-select-empty--muted">
                        {t('popup.expertOrgSelect.selectTaskFirstEmpty')}
                      </li>
                    ) : useServerProposalTargets && effectiveProposalDemandId && !proposalSearchQuery ? (
                      <li className="expert-org-select-empty expert-org-select-empty--muted">
                        {t('popup.expertOrgSelect.searchNameHint')}
                      </li>
                    ) : useServerProposalTargets && serverLoading && listRows.length === 0 ? (
                      <li className="expert-org-select-empty">{t('popup.common.processing')}</li>
                    ) : listRows.length === 0 ? (
                      <li className="expert-org-select-empty">{t('popup.expertOrgSelect.noSearchResult')}</li>
                    ) : (
                      listRows.map((item) => (
                        <li key={item.id} className="expert-org-select-item">
                          <label className="expert-org-select-row">
                            <input
                              type="checkbox"
                              checked={selectedIds.has(item.id)}
                              onChange={() => toggle(item.id)}
                            />
                            <span className="expert-org-select-author-wrap">
                              <AuthorDisplay
                                regUid={item.skipUserFetch ? undefined : item.id}
                                name={item.name || ''}
                                userId={item.userId || ''}
                                authorCd={item.authorCd || ''}
                                skipUserFetch={item.skipUserFetch === true}
                                className="expert-org-select-author-display"
                              />
                            </span>
                            <span className="expert-org-select-member-type">
                              {item.memberTypeLabel || item.type || '회원'}
                            </span>
                          </label>
                          {item.email && sendMode === 'EMAIL' && (
                            <p className="expert-org-select-email-row">{item.email}</p>
                          )}
                        </li>
                      ))
                    )}
                  </ul>
                  {(!useServerProposalTargets || (effectiveProposalDemandId && proposalSearchQuery)) && listTotalPages > 1 && (
                    <div className="expert-org-select-pagination">
                      <Pagination
                        currentPage={listSafePage}
                        totalPages={listTotalPages}
                        onPageChange={setCurrentPage}
                      />
                    </div>
                  )}
                </>
              ) : null}
            </div>
          )}

          {!messageInTopSplit ? (
            <div
              className={`expert-org-select-message-area d-flex flex-column min-w-0 ${
                !showExpertList ? 'col-12' : 'col-6'
              }`}
            >
              {proposalMessageColumn}
            </div>
          ) : null}
        </div>
        )}
      </div>
      <div className="main-popup-footer d-flex justify-content-between">
        <button
          type="button"
          className={`btn main-popup-left-btn ${getButtonClass(config?.leftButton?.color || 'btn-secondary')}`}
          onClick={handleCancel}
        >
          {config?.leftButton?.text ?? t('popup.common.cancel')}
        </button>
        <button
          type="button"
          className={`btn main-popup-right-btn ${getButtonClass(config?.rightButton?.color || 'btn-primary')}`}
          onClick={handleConfirm}
          disabled={!hasSelection || (showTaskSelector && !selectedTask)}
        >
          {config?.rightButton?.text ?? t('popup.expertOrgSelect.sendMessage')}
        </button>
      </div>
      <style>{`
        .popup-expert-org-select.popupContent {
          display: flex;
          flex-direction: column;
          min-height: 0;
        }
        .popup-expert-org-select .container {
          display: flex;
          flex-direction: column;
          flex: 1;
          min-height: 0;
        }
        .popup-expert-org-select-body {
          padding: 0 8px 8px;
          min-height: 0;
          flex: 1 1 auto;
          overflow-y: auto;
        }
        .popup-expert-org-select .main-popup-footer {
          flex-shrink: 0;
        }
        .expert-org-select-proposal-split {
          margin-bottom: 8px;
          min-height: 0;
        }
        .expert-org-select-proposal-split-left {
          min-width: 0;
        }
        .expert-org-select-proposal-split-left .expert-org-select-task-selector-top {
          flex: 0 1 auto;
          min-height: 0;
        }
        .expert-org-select-task-list-panel {
          display: flex;
          flex-direction: column;
          width: 100%;
          flex: 0 0 auto;
          height: 318px;
          min-height: 318px;
          max-height: 318px;
          box-sizing: border-box;
        }
        .expert-org-select-proposal-split-left .expert-org-select-task-list-panel .expert-org-select-task-list {
          flex: 1 1 auto;
          min-height: 0;
          max-height: none;
          overflow: auto;
        }
        .expert-org-select-task-pagination-slot {
          flex: 0 0 48px;
          min-height: 48px;
          max-height: 48px;
          display: flex;
          align-items: center;
          justify-content: center;
          box-sizing: border-box;
        }
        .expert-org-select-proposal-split-left .expert-org-select-task-pagination {
          margin-top: 0;
        }
        .expert-org-select-proposal-split-left .expert-org-select-task-list-row {
          min-height: 52px;
        }
        .expert-org-select-proposal-split-right {
          min-width: 0;
          min-height: 0;
        }
        .expert-org-select-proposal-split-right .expert-org-select-task-box {
          flex: 1;
          min-height: 0;
          display: flex;
          flex-direction: column;
        }
        .expert-org-select-proposal-split-right .expert-org-select-message-row {
          flex: 1;
          min-height: 72px;
          display: flex;
        }
        .expert-org-select-proposal-split-right .expert-org-select-message-input {
          flex: 1;
          min-height: 100px;
        }
        .expert-org-select-task-selector-top {
          display: flex;
          flex-direction: column;
          gap: 6px;
          padding: 8px 10px;
          border: 1px solid #e2e8f0;
          border-radius: 6px;
          background: #f8fafc;
          min-width: 0;
          min-height: 0;
          box-sizing: border-box;
        }
        .expert-org-select-task-selector-label {
          font-size: 12px;
          font-weight: 600;
          color: #475569;
          margin: 0;
          white-space: nowrap;
          letter-spacing: -0.01em;
        }
        .expert-org-select-task-selector-top .expert-org-select-task-combo {
          flex: 0 0 auto;
          width: 100%;
          min-height: 0;
        }
        .expert-org-select-task-search-input {
          height: auto;
        }
        .expert-org-select-task-combo { max-width: 100%; }
        .expert-org-select-source-radios { display: flex; flex-wrap: wrap; gap: 12px; margin-bottom: 2px; }
        .expert-org-select-source-radio { display: inline-flex; align-items: center; gap: 4px; font-size: 12px; color: #334155; cursor: pointer; }
        .expert-org-select-task-list {
          list-style: none;
          margin: 0;
          padding: 0;
          border: 1px solid #e2e8f0;
          border-radius: 6px;
          background: #fff;
          max-height: 200px;
          overflow: auto;
        }
        .expert-org-select-task-list-item { border-bottom: 1px solid #f1f5f9; }
        .expert-org-select-task-list-item:last-child { border-bottom: none; }
        .expert-org-select-task-list-row {
          display: flex;
          align-items: flex-start;
          gap: 10px;
          padding: 8px 10px;
          margin: 0;
          cursor: pointer;
          width: 100%;
          box-sizing: border-box;
        }
        .expert-org-select-task-list-row input[type="radio"] {
          flex-shrink: 0;
          width: 16px;
          height: 16px;
          margin: 2px 0 0 0;
          cursor: pointer;
          accent-color: var(--theme-color-primary, #6366f1);
        }
        .expert-org-select-task-list-title {
          flex: 1;
          min-width: 0;
          font-size: 13px;
          line-height: 1.45;
          color: #1e293b;
          display: -webkit-box;
          -webkit-box-orient: vertical;
          -webkit-line-clamp: 2;
          line-clamp: 2;
          overflow: hidden;
          word-break: break-word;
        }
        .expert-org-select-task-pagination { margin-top: 6px; }
        .expert-org-select-list-filter { margin-bottom: 4px; }
        .expert-org-select-guest-input-row {
          display: flex;
          gap: 8px;
          margin-bottom: 6px;
        }
        .expert-org-select-guest-input {
          flex: 1;
        }
        .expert-org-select-guest-add-btn {
          width: 40px;
          height: 38px;
          min-width: 40px;
          padding: 0;
          border: 1px solid var(--theme-color-primary);
          border-radius: 8px;
          background: var(--theme-color-primary);
          color: #fff;
          font-size: 22px;
          font-weight: 500;
        }
        .expert-org-select-guest-add-btn:hover {
          background: var(--theme-color-primary);
          border-color: var(--theme-color-primary);
          color: #fff;
        }
        .expert-org-select-send-mode-tabs-wrap {
          width: 100%;
          margin-bottom: 10px;
        }
        .expert-org-select-send-mode-tabs.tabs-container--pill {
          width: 100%;
          margin-bottom: 0;
        }
        .expert-org-select-recipient-tabs-wrap {
          width: 100%;
          margin-bottom: 8px;
        }
        .expert-org-select-recipient-tabs.tabs-container--segmented {
          width: 100%;
          align-self: stretch;
          justify-content: center;
          margin-bottom: 0;
        }
        .expert-org-select-list-combo { width: 100%; }
        .expert-org-select-task-selector-hint {
          font-size: 12px;
          color: #64748b;
          margin: 0 0 8px 0;
          padding: 0 2px;
        }
        .expert-org-select-task-info-title {
          font-size: 13px;
          font-weight: 600;
          color: #1e293b;
          margin: 0 0 2px 0;
          line-height: 1.35;
        }
        .expert-org-select-task-info-meta-line {
          font-size: 12px;
          color: #64748b;
          line-height: 1.35;
        }
        .expert-org-select-view-detail-btn {
          flex-shrink: 0;
          align-self: center;
          padding: 4px 8px;
          font-size: 12px;
          font-weight: 500;
          color: #475569;
          background: #fff;
          border: 1px solid #e2e8f0;
          border-radius: 4px;
          cursor: pointer;
        }
        .expert-org-select-view-detail-btn:hover {
          background: #f1f5f9;
          color: #334155;
          border-color: #cbd5e1;
        }
        .expert-org-select-layout {
          min-height: 0;
          max-height: 52vh;
        }
        .expert-org-select-layout--only-selected .expert-org-select-message-area {
          width: 100%;
        }
        .expert-org-select-message-area { display: flex; flex-direction: column; min-width: 0; min-height: 0; }
        .expert-org-select-message-title {
          font-size: 14px;
          font-weight: 600;
          color: #1f2937;
          margin: 0 0 8px 0;
        }
        .expert-org-select-task-box {
          flex: 1;
          min-height: 0;
          border: 1px solid #e5e7eb;
          border-radius: 6px;
          padding: 10px 12px;
          background: #f9fafb;
        }
        .expert-org-select-task-title {
          font-size: 12px;
          font-weight: 600;
          color: #6b7280;
          margin-bottom: 8px;
          letter-spacing: -0.01em;
        }
        .expert-org-select-task-info-in-box {
          display: flex;
          flex-direction: column;
          gap: 4px;
          margin-bottom: 8px;
          padding-bottom: 8px;
          border-bottom: 1px solid #e5e7eb;
        }
        .expert-org-select-task-info-title-row {
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .expert-org-select-task-info-in-box .expert-org-select-task-info-title {
          flex: 1;
          min-width: 0;
          margin: 0;
          font-size: 13px;
          font-weight: 600;
          color: #1e293b;
        }
        .expert-org-select-task-info-in-box .expert-org-select-task-info-meta-line {
          font-size: 12px;
          color: #64748b;
        }
        .expert-org-select-task-info-in-box .expert-org-select-view-detail-btn {
          flex-shrink: 0;
        }
        .expert-org-select-field-row {
          margin-bottom: 6px;
          font-size: 13px;
        }
        .expert-org-select-field-row:last-of-type { margin-bottom: 0; }
        .expert-org-select-field-label {
          display: inline-block;
          min-width: 80px;
          font-size: 12px;
          font-weight: 600;
          color: #6b7280;
          letter-spacing: -0.01em;
        }
        .expert-org-select-field-value {
          font-size: 13px;
          color: #111;
          word-break: break-word;
        }
        .expert-org-select-field-row--author {
          display: flex;
          align-items: flex-start;
          gap: 10px;
        }
        .expert-org-select-field-row--author .expert-org-select-field-label {
          flex-shrink: 0;
          padding-top: 4px;
        }
        .expert-org-select-field-value--authors {
          display: flex;
          flex-direction: column;
          gap: 10px;
          flex: 1;
          min-width: 0;
        }
        .expert-org-select-field-placeholder {
          font-size: 13px;
          color: #9ca3af;
        }
        .expert-org-select-task-box .expert-org-select-author-display {
          max-width: 100%;
        }
        .expert-org-select-guest-chip {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          width: fit-content;
          max-width: 100%;
          padding: 5px 10px;
          border-radius: 999px;
          border: 1px solid #dbe1ea;
          background: #f8fafc;
        }
        .expert-org-select-guest-email {
          font-size: 12px;
          color: #334155;
          font-weight: 600;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          max-width: 260px;
        }
        .expert-org-select-guest-remove {
          border: none;
          background: transparent;
          color: #64748b;
          font-size: 16px;
          line-height: 1;
          padding: 0;
          cursor: pointer;
        }
        .expert-org-select-guest-remove:hover {
          color: #ef4444;
        }
        .expert-org-select-message-row { margin: 4px 0; }
        .expert-org-select-message-input {
          width: 100%;
          border: 1px solid #d1d5db;
          border-radius: 4px;
          padding: 6px 8px;
          font-size: 13px;
          line-height: 1.4;
          resize: none;
          min-height: 0;
          background: #fff;
        }
        .expert-org-select-message-input:disabled {
          background: #f3f4f6;
          color: #9ca3af;
        }
        .expert-org-select-list-area {
          display: flex;
          flex-direction: column;
          min-width: 0;
          min-height: 0;
          overflow: hidden;
        }
        .expert-org-select-toolbar {
          display: flex;
          align-items: center;
          justify-content: flex-end;
          gap: 8px;
          padding: 4px 0 6px;
          border-bottom: 1px solid #e5e7eb;
          margin-bottom: 6px;
        }
        .expert-org-select-hint { font-size: 11px; color: #9ca3af; }
        .expert-org-select-hint--only { font-size: 12px; color: #64748b; }
        .expert-org-select-list {
          list-style: none;
          padding: 0;
          margin: 0;
          overflow: hidden;
          flex: 1;
          min-height: 0;
          display: flex;
          flex-direction: column;
        }
        .expert-org-select-item {
          border-bottom: 1px solid #f3f4f6;
          padding: 3px 0;
          flex-shrink: 0;
        }
        .expert-org-select-item:last-child { border-bottom: none; }
        .expert-org-select-row {
          display: flex;
          align-items: center;
          gap: 8px;
          flex-wrap: nowrap;
          cursor: pointer;
        }
        .expert-org-select-row input {
          width: 16px;
          height: 16px;
          flex-shrink: 0;
          align-self: center;
        }
        .expert-org-select-author-wrap {
          flex: 1;
          min-width: 0;
          overflow: hidden;
          display: flex;
          align-items: center;
        }
        .expert-org-select-author-display .alli-author-name {
          font-weight: 600;
          font-size: 13px;
          color: #111;
        }
        .expert-org-select-author-display .alli-author-org {
          font-size: 12px;
          color: #64748b;
        }
        .expert-org-select-member-type {
          flex-shrink: 0;
          font-size: 12px;
          font-weight: 600;
          color: #6b7280;
          white-space: nowrap;
          letter-spacing: -0.02em;
          align-self: center;
          line-height: 1.25;
        }
        .expert-org-select-name { font-weight: 600; font-size: 13px; color: #111; min-width: 80px; }
        .expert-org-select-type { font-size: 12px; color: #6b7280; }
        .expert-org-select-field { font-size: 12px; color: #374151; }
        .expert-org-select-region { font-size: 12px; color: #6b7280; }
        .expert-org-select-email-row {
          font-size: 11px;
          color: #4f46e5;
          margin: 2px 0 0 24px;
          line-height: 1.3;
          word-break: break-all;
        }
        .expert-org-select-empty { padding: 10px 8px; text-align: center; font-size: 13px; color: #94a3b8; list-style: none; }
        .expert-org-select-empty--muted { color: #64748b; line-height: 1.45; }
        .expert-org-select-pagination { margin-top: 6px; padding-top: 6px; border-top: 1px solid #e5e7eb; flex-shrink: 0; }
        @media (max-width: 768px) {
          .popup-expert-org-select-body .expert-org-select-proposal-split > .col-6,
          .popup-expert-org-select-body .expert-org-select-layout > .col-6 {
            flex: 0 0 100%;
            max-width: 100%;
          }
        }
      `}</style>
    </PopupWrapper>
  );
};

export default ExpertOrgSelectPopup;
